import clsx from "clsx";
import type { TravelSegment } from "../../utils/travel";
import { formatTravelEstimate } from "../../utils/geo";

interface TravelChipProps {
  segment: TravelSegment;
  /** Vertical center of the gap between the two blocks, in px */
  y: number;
}

/**
 * Travel estimate pill rendered in the gap between two consecutive blocks.
 * Warning tone when the estimated travel exceeds the actual gap.
 * Informational only — never blocks a mutation.
 */
export function TravelChip({ segment, y }: TravelChipProps) {
  const { estimate, gapMinutes, overrun } = segment;

  return (
    <div
      className={clsx(
        "absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-10",
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5",
        "text-2xs font-bold shadow-sm pointer-events-none select-none",
        overrun
          ? "bg-error-bg-strong border-error-bg-strong text-error"
          : "bg-surface border-border text-secondary",
      )}
      style={{ top: y }}
      title={
        overrun
          ? `Estimated ${estimate.travelMinutes} min of travel, but only ${Math.max(Math.round(gapMinutes), 0)} min between blocks`
          : `Estimated travel to the next stop`
      }
    >
      <span
        className="material-symbols-outlined text-xs leading-none"
        aria-hidden
      >
        {estimate.icon}
      </span>
      <span>
        {overrun
          ? `${estimate.travelMinutes} min ${estimate.mode} · ${Math.max(Math.round(gapMinutes), 0)} min gap`
          : formatTravelEstimate(estimate)}
      </span>
    </div>
  );
}
